import Image from 'next/image';
import { faChevronRight } from '@fortawesome/pro-light-svg-icons';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';
import type { BlogPost, Maybe } from '@medprev-web-app/api-graphql-client';

import bgPost from './bg-post.png';

import { Card } from '@/components/ui/card.component';
import { Link } from '@/components/ui/link/link.component';
import { fromNow } from '@/utils/formatters/format-from-now';
import { formatISO8601toBRDate } from '@/utils/formatters/format-iso-8601-to-br-date';
import { stripMarkdown } from '@/utils/formatters/stripMarkdown';
import { Icon } from '@/components/ui/icon.component';
import { DecorativePill } from '@/components/ui/decorative-pill.component';

interface PostCardProps {
  post: Maybe<BlogPost>;
  variant?: 'default' | 'featured';
}

export function PostCard({ post, variant = 'default' }: PostCardProps) {
  const isFeatured = variant === 'featured';

  return (
    <Link
      prefetch={false}
      href={`/blog/${post!.slug}/`}
      key={post!.id}
      className="w-full group"
      data-testid={`${post!.id}${DataTestIdEnum.BLOG_POST_LINK}`}
    >
      <Card className="mt-6 flex flex-col px-0 py-0 hover:bg-green-50 h-full ">
        <div className="relative">
          <Image
            src={post!.coverPhoto?.url || bgPost}
            alt={post!.coverPhoto?.alternativeText || 'Imagem de capa do post'}
            width={post!.coverPhoto?.width || 3840}
            height={post!.coverPhoto?.height || 2025}
            className={`object-cover w-full rounded-t-lg ${isFeatured ? 'h-64' : 'h-48'}`}
          />
        </div>
        <div className="px-6 flex flex-col flex-1">
          <div className="flex flex-row gap-2 items-center">
            <DecorativePill
              label={post!.subject?.name || ''}
              variant="info"
              className="mt-6 truncate"
              data-testid={`${post!.id}${DataTestIdEnum.BLOG_POST_CARD_THEME_PILL}`}
            />
            {isFeatured && (
              <div className="mt-6">
                <DecorativePill label="Post novo" variant="success" />
              </div>
            )}
          </div>
          <p
            className={`mt-4 mb-2 text-gray-900 font-bold line-clamp-2 ${
              isFeatured ? 'text-xl md:text-lg' : 'text-xl md:text-lg xl:h-14'
            }`}
            data-testid={`${post!.id}${DataTestIdEnum.BLOG_POST_TITLE}`}
          >
            {post!.title}
          </p>
          <div>
            <span
              className="line-clamp-3 text-gray-600"
              data-testid={`${post!.id}${DataTestIdEnum.BLOG_POST_CONTENT}`}
            >
              {stripMarkdown(post!.content || '')}
            </span>
          </div>
          <div className="flex justify-start mt-1">
            <span className="text-green-500 font-semibold text-left group-hover:underline flex items-center gap-2">
              Continuar lendo
              <Icon
                icon={faChevronRight}
                className="ml-1 group-hover:translate-x-1 transition-transform duration-300"
              />
            </span>
          </div>
          <div
            className={`${
              isFeatured ? 'mt-4' : 'mt-auto'
            } mb-6 text-gray-600 font-normal text-xs justify-between flex items-center`}
            data-testid={`${post!.id}${DataTestIdEnum.BLOG_POST_DATE}`}
          >
            {formatISO8601toBRDate(post!.publishDate || '')}
            {' '}
            -
            {' '}
            {fromNow(new Date(post!.publishDate || ''))}
          </div>
        </div>
      </Card>
    </Link>
  );
}
